<style lang="scss" scoped>
.sync {
	&__header {
		@include flex-center;
		&__actions {
			margin-left: auto;
		}
	}
	&__description {
		padding-top: 0;
		margin-top: 0;
		padding-bottom: 15px;
	}
	&__row {
		margin-bottom: 20px;
	}
	&__number {
		font-weight: 600;
		font-size: 20px;
		margin-right: 12px;
		&.--additions {
			color: rgb(47, 169, 91);
		}
		&.--modifications {
			color: rgb(39, 135, 239);
		}
		&.--deletions {
			color: rgb(255, 186, 52);
		}
		&.--errors {
			color: rgb(240, 58, 56);
		}
	}
	&__sub-header {
		font-size: 20px;
	}
	&__show-more {
		margin-left: auto;
	}
	&__table {
		td {
			width: 50%;
			&:last-of-type {
				text-align: right;
				padding-right: 18px;
			}
		}
		tr:last-of-type td {
			border-bottom: none;
		}
	}
	&__tag {
		margin-left: 12px;
	}
	& ::v-deep .el-card__body {
		padding: 0;
	}
	& ::v-deep .el-collapse {
		.el-collapse-item__header {
			padding-left: 18px;
			font-size: 16px;
		}
		.el-collapse-item__arrow {
			margin-right: 18px;
		}
		.el-collapse-item__content {
			padding-bottom: 0;
		}
	}
}
.progress-bar {
	margin: 40px 0px 0px 0px;
}
</style>
<template lang="pug">
.sync
	app-header
		.sync__header(slot="header")
			h1 Sync
			.sync__header__actions
				el-button(v-if="!results.Failed", type="success", @click="runSync", :type="hasSyncLoaded && !results.Failed ? 'success' : 'danger'", :loading="!hasSyncLoaded && !results.Failed") {{ hasSyncLoaded && !results.Failed ? 'Run sync' : 'Loading...' }}
				el-button(v-if="hasSyncLoaded && !results.Failed", @click="download", type="primary") Export
	.container 
		.main-panel
			div(v-if="!hasSyncLoaded")
				loading(:message="loadingMessages")
					el-progress.progress-bar(:percentage="progressPercentage", :text-inside="true", :stroke-width="18", text-color="#fff")
			.sync.main-panel__padding(v-else-if="results.Failed")
				el-alert(title="An error occurred while calculating sync", type="error")
			.sync.main-panel__padding(v-else, e2e="sync-loaded")
				p.sync__description Edusync will perform the following actions on your {{ providerName }} Directory. Make sure you have ignored or matched existing users, groups and classes to prevent unwanted changes. Once you have reviewed the changes, click <b>Run sync</b>.

				el-card.sync__row(
					v-for="(data, key) of {additions: results.Additions, modifications: results.Modifications, deletions: results.Deletions, errors: results.Errors }"
				)
					div(slot="header")
						.d-flex.align-items-center
							span.sync__number(:class="`--${key}`") {{ results.Meta[`${key}.total`] || data.length }}
							span.sync__sub-header {{ key }}
							el-button.sync__show-more(v-show="data.length", @click="data.show = !data.show") {{ data.show ? 'hide' : 'show' }}
					div(v-show="data.show")
						el-collapse
							el-collapse-item(v-for="(rows, title) of data.rows")
								template(slot="title") 
									| {{ title | friendy_type | capitalize }}
									el-tag.sync__tag(size="mini") {{ results.Meta[`${key}.${title}`] || rows.length }}
								sync-summary-data(:rows="rows")
</template>
<script>
import gql from "graphql-tag";
import Bugsnag from '@bugsnag/js';
import _ from "lodash";
import csv from "@/libs/csv";
import friendlyType from "../../filters/FriendlyType";
import moment from "moment";
import SyncSummaryData from "../includes/SyncSummaryData.vue";

export default {
	components: {
		SyncSummaryData
	},

	data() {
		return {
			results: {
				Additions: [],
				Modifications: [],
				Deletions: [],
				Errors: [],
				Running: false,
				Ready: false,
				Failed: false,
			},
			pollResults: false,
			uuid: null,
			progressPercentage: 0,
			loadingMessages: ["This may take a few minutes...", "Calculating changes..."],
		};
	},

	async mounted() {
		// Fetch our sync uuid
		const {
			data: { CalculateSyncWork: uuid },
		} = await this.$apollo.mutate({
			mutation: gql`
				mutation CalculateSyncWork($useCache: Boolean!) {
					CalculateSyncWork(useCache: $useCache)
				}
			`,
			variables: {
				useCache: this.$route.query.use_cache == 1 || false,
			}
		});
		this.uuid = uuid;
		// Start the query to poll results till it's done.
		this.pollResults = true;
	},

	computed: {
		hasSyncLoaded() {
			return !this.$apollo.loading && this.pollResults == false;
		},
	},

	methods: {
		async runSync() {
			try {
				await this.$apollo.mutate({
					mutation: gql`
						mutation DoSyncWork($uuid: String!) {
							DoSyncWork(uuid: $uuid)
						}
					`,
					variables: {
						uuid: this.uuid,
					},
				});
				this.$router.push({
					name: "directory.sync.run",
					params: {
						uuid: this.uuid,
					},
				});
			} catch (error) {
				Bugsnag.notify(JSON.stringify(error, null, 2));
				console.error("Mutation error", error);
				this.$message.error("Failed to start sync");
			}
		},
		download() {
			let csvObject = [];
			const summary = {additions: this.results.Additions, modifications: this.results.Modifications, deletions: this.results.Deletions, errors: this.results.Errors };

			_(summary).forEach((data, key) => {
				csvObject.push([_.upperFirst(key), this.results.Meta[`${key}.total`] || data.length]);
			});
			csvObject.push([null], ["Type", "Change", "Data", "Action"]);
			_(summary).forEach((data, key) => {
				_(data.rows).forEach((rows, title) => {
					_(rows).forEach(row => {
						csvObject.push([_.upperFirst(friendlyType(title)), _.upperFirst(key.slice(0, -1)), _.upperFirst(row.name), row.message]);
					});
				});
			});
			csv.exportToCsv(`sync-${moment().format("YYYY-MM-DD_HH:mm")}.csv`, csvObject);
		},
	},

	apollo: {
		results: {
			query: gql`
				query GetSync($uuid: String!) {
					Sync(uuid: $uuid) {
						id
						Additions {
							id
							name
							message
							type
						}
						Modifications {
							id
							name
							message
							before_action
							after_action
							type
						}
						Deletions {
							id
							name
							message
							type
						}
						Errors {
							id
							name
							message
							type
						}
						Running
						Ready
						Failed
						Progress
						ProgressPercentage
						Meta
					}
				}
			`,
			fetchPolicy: "no-cache",
			variables() {
				return {
					uuid: this.uuid,
				};
			},
			pollInterval: 3000,
			update(r) {
				if (r.Sync) {
					if (r.Sync.Ready) {
						// There is now a result, stop polling for updates
						this.pollResults = false;
						// If result has already started, redirect straight to activity
						if (r.Sync.Running) {
							this.$router.push({
								name: "directory.sync.run",
								params: {
									uuid: this.uuid,
								},
							});
						}
						if (r.Sync.Failed) return r.Sync;
						r.Sync.Meta = JSON.parse(r.Sync.Meta);
						const [additions, modifications, deletions, errors] = _.map([r.Sync.Additions, r.Sync.Modifications, r.Sync.Deletions, r.Sync.Errors], (changes) =>
							_.groupBy(changes, "type")
						);
						return {
							...r.Sync,
							Additions: { rows: additions, length: r.Sync.Additions.length, show: false },
							Modifications: { rows: modifications, length: r.Sync.Modifications.length, show: false },
							Deletions: { rows: deletions, length: r.Sync.Deletions.length, show: false },
							Errors: { rows: errors, length: r.Sync.Errors.length, show: false },
						};
					} else if (r.Sync.Progress) {
						// Update progress message
						this.loadingMessages = this.loadingMessages.slice(0, 1);
						this.progressPercentage = r.Sync.ProgressPercentage || 0;
						this.loadingMessages.push(r.Sync.Progress);
					}
					return r.Sync;
				}
			},
			skip() {
				// Skip the query until we have a uuid
				return this.pollResults == false;
			},
		},
	},
};
</script>
